/* hero-video.js — <hero-video> (sections/hero-video.liquid).

   Progressive enhancement over a hero that is already complete without it:

   * Poster -> video. The still paints first; the video is opacity 0 until its
     first `playing` event, then cross-fades in. Refused autoplay (data saver,
     low-power mode) is not an error: the still simply stays.
   * Play only while on screen, and never for a visitor who asked for reduced
     motion — followed live, not just read once at load.
   * A pause/play toggle for the autoplaying loop (WCAG 2.2.2). An explicit
     choice wins over both of the rules above until the visitor changes it.
   * The reveal frame: the box scales from 1 to viewport width / box width while
     its inner counter-scales by the reciprocal, so the clip opens to full bleed
     and the picture inside holds its size. The endpoint is derived from the
     measured box on every resize, never authored.

   Loaded as a module from the section, so the browser runs it once however many
   heroes the page has. */

const REVEAL_START = 0.7; // opening starts when the frame's top reaches 70% of the viewport
const REVEAL_SPAN = 0.6; // and settles after a further 60% of the viewport
const REVEAL_EPSILON = 0.0005;

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class HeroVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('[data-hero-video]');
    this.toggle = this.querySelector('[data-hero-toggle]');
    /* null = follow the automatic rules; true/false = the visitor's choice. */
    this.choice = null;
    this.inView = true;

    this.onMotionChange = () => {
      this.syncPlayback();
      this.syncReveal();
    };
    reducedMotion.addEventListener('change', this.onMotionChange);

    this.initReveal();
    if (this.video) this.initVideo();
  }

  disconnectedCallback() {
    reducedMotion.removeEventListener('change', this.onMotionChange);
    if (this.observer) this.observer.disconnect();
    if (this.video) {
      this.video.removeEventListener('playing', this.onPlaying);
      this.video.removeEventListener('pause', this.onPauseChange);
      this.video.removeEventListener('play', this.onPauseChange);
    }
    this.teardownReveal();
  }

  /* --- video ------------------------------------------------------------- */

  initVideo() {
    const video = this.video;
    this.onPlaying = () => {
      video.classList.add('is-playing');
      this.syncToggle();
    };
    this.onPauseChange = () => this.syncToggle();
    video.addEventListener('playing', this.onPlaying);
    video.addEventListener('pause', this.onPauseChange);
    video.addEventListener('play', this.onPauseChange);

    if (this.toggle) {
      this.toggle.hidden = false;
      this.toggle.addEventListener('click', () => {
        this.choice = video.paused;
        this.syncPlayback();
      });
    }

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.inView = entry.isIntersecting;
          this.syncPlayback();
        });
      });
      this.observer.observe(this);
    }

    /* Already buffered and playing before the module ran (it autoplays from
       the attribute): reveal on the next frame so the fade has a start. */
    if (!video.paused && video.readyState >= 3) {
      window.requestAnimationFrame(this.onPlaying);
    }
    this.syncPlayback();
  }

  get shouldPlay() {
    if (!this.inView) return false;
    if (this.choice !== null) return this.choice;
    return !reducedMotion.matches;
  }

  syncPlayback() {
    const video = this.video;
    if (!video) return;
    if (this.shouldPlay) {
      video.preload = 'auto';
      const attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(() => {
          /* Autoplay refused — the still remains, which is a whole hero. */
          this.syncToggle();
        });
      }
    } else {
      video.pause();
    }
    this.syncToggle();
  }

  syncToggle() {
    if (!this.toggle || !this.video) return;
    const paused = this.video.paused;
    this.toggle.toggleAttribute('data-paused', paused);
    const label = paused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause;
    if (label) this.toggle.setAttribute('aria-label', label);
  }

  /* --- reveal ------------------------------------------------------------ */

  initReveal() {
    if (!this.hasAttribute('data-reveal')) return;
    this.revealFrame = this.querySelector('[data-hero-frame]');
    this.revealBox = this.querySelector('[data-hero-box]');
    this.revealInner = this.querySelector('[data-hero-inner]');
    if (!this.revealFrame || !this.revealBox || !this.revealInner) return;

    this.revealRaf = 0;
    this.revealLast = -1;
    this.applyReveal = this.applyReveal.bind(this);
    this.onRevealScroll = () => {
      if (this.revealRaf) return;
      this.revealRaf = window.requestAnimationFrame(this.applyReveal);
    };
    /* A resize moves the endpoint even when the eased value has not changed,
       so the skip-if-unchanged memo has to be cleared. */
    this.onRevealResize = () => {
      this.revealLast = -1;
      this.onRevealScroll();
    };
    this.syncReveal();
  }

  /* Listeners only while motion is allowed; under reduced motion the frame
     stays exactly as the section rendered it. */
  syncReveal() {
    if (!this.revealBox) return;
    if (reducedMotion.matches) {
      this.teardownReveal();
      this.revealBox.style.removeProperty('transform');
      this.revealInner.style.removeProperty('transform');
      return;
    }
    if (this.revealListening) return;
    window.addEventListener('scroll', this.onRevealScroll, { passive: true });
    window.addEventListener('resize', this.onRevealResize);
    this.revealListening = true;
    this.revealLast = -1;
    this.applyReveal();
  }

  teardownReveal() {
    if (this.revealRaf) {
      window.cancelAnimationFrame(this.revealRaf);
      this.revealRaf = 0;
    }
    if (!this.revealListening) return;
    window.removeEventListener('scroll', this.onRevealScroll);
    window.removeEventListener('resize', this.onRevealResize);
    this.revealListening = false;
  }

  applyReveal() {
    this.revealRaf = 0;
    const viewport = window.innerHeight;
    const rect = this.revealFrame.getBoundingClientRect();
    /* More than a viewport away: nothing to see, so nothing to write. */
    if (rect.bottom < -viewport || rect.top > viewport * 2) return;

    const raw = (REVEAL_START * viewport - rect.top) / (REVEAL_SPAN * viewport);
    const t = Math.min(1, Math.max(0, raw));
    const eased = t * t; // easeInQuad, as the source
    if (Math.abs(eased - this.revealLast) < REVEAL_EPSILON) return;
    this.revealLast = eased;

    const base = this.revealBox.offsetWidth || 1;
    /* clientWidth, not innerWidth: a classic scrollbar is not part of the
       width the frame has to reach. */
    const target = document.documentElement.clientWidth || window.innerWidth;
    const maxScale = Math.max(1, target / base);
    const scale = 1 + (maxScale - 1) * eased;
    this.revealBox.style.transform = `scale(${scale})`;
    this.revealInner.style.transform = `scale(${maxScale / scale})`;
  }
}

if (!customElements.get('hero-video')) customElements.define('hero-video', HeroVideo);
